/**
 * Phase 26 — a Block hour rule's scope (user, 12/10/2026: "the ones who has
 * these tags can go through even if its blocked hours, and vice versa"): the
 * block acts only on the groups whose list of the day carries one of the
 * tags (only_tagged), or on every other group (except_tagged). A decision
 * is provable or it is not taken: a list whose tags could not be read goes
 * through — missing data never acts.
 * For Media Devoted | Created By Ciro Autiero
 */
import { DROP_GEN_RULE_LIMITS, DROP_GEN_SCOPE_LISTS, type DropGenRuleCheck, type DropGenRuleScope } from "./rules.types";

/** A scope that names no tag yet is not complete: the rule does nothing until one is picked. */
export const scopeIncomplete = (scope: DropGenRuleScope | undefined): boolean => scope !== undefined && scope.tags.length === 0;

/** Does the block act on a drop whose list carries `listTags`? null = the tags are unknown → never. */
export function blockAppliesTo(scope: DropGenRuleScope | undefined, listTags: readonly string[] | null): boolean {
  if (!scope) return true;
  if (listTags === null || scopeIncomplete(scope)) return false;
  const tagged = scope.tags.some((t) => listTags.includes(t));
  return scope.lists === "only_tagged" ? tagged : !tagged;
}

const names = (tags: readonly string[]) => tags.map((t) => `"${t}"`).join(" or ");

/** "for lists tagged "LOW CPUC"" · "for every list except those tagged "HIGH VALUE"". */
export function describeScope(scope: DropGenRuleScope | undefined): string {
  if (!scope) return "";
  return scope.lists === "only_tagged" ? `for lists tagged ${names(scope.tags)}` : `for every list except those tagged ${names(scope.tags)}`;
}

/** Why one drop went through or was held, in the warning's words. */
export function scopeVerdictWords(scope: DropGenRuleScope, listTags: readonly string[] | null): string {
  if (listTags === null) return "its list's tags could not be read";
  const hit = scope.tags.filter((t) => listTags.includes(t));
  return hit.length > 0 ? `its list is tagged ${names(hit)}` : `its list is not tagged ${names(scope.tags)}`;
}

/** The scope's problems in words — empty when valid (or absent). */
export function scopeErrors(r: Record<string, unknown>, at: string, check: DropGenRuleCheck): string[] {
  if (r.scope === undefined || r.scope === null) return [];
  if (!check.allowScope) return [`${at}: a list scope is for hour rules only`];
  if (r.action !== "block") return [`${at}: a list scope is for the Block action only`];
  const s = r.scope as Partial<DropGenRuleScope>;
  const errs: string[] = [];
  if (!DROP_GEN_SCOPE_LISTS.includes(s.lists as never)) errs.push(`${at}: the scope's lists must be one of ${DROP_GEN_SCOPE_LISTS.join(", ")}`);
  const tags = Array.isArray(s.tags) ? s.tags : null;
  if (!tags || tags.length > DROP_GEN_RULE_LIMITS.scopeTags) errs.push(`${at}: the scope names at most ${DROP_GEN_RULE_LIMITS.scopeTags} tags`);
  else if (tags.some((t) => typeof t !== "string" || !t.trim() || t.length > DROP_GEN_RULE_LIMITS.tagName))
    errs.push(`${at}: each scope tag is a name of 1 to ${DROP_GEN_RULE_LIMITS.tagName} characters`);
  return errs;
}
